import React, { useState, useCallback } from 'react';
import { Chip, Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { copyToClipboard } from '../../utils/copyToClipboard';

export type PhoneLinkVariant = 'customer' | 'store';

export interface PhoneLinkChipProps {
  phone: string;
  variant: PhoneLinkVariant;
  /** תווית נוספת בתוך הצ'יפ, למשל (חנות) */
  suffix?: React.ReactNode;
  /** תוכן מותאם (הדגשת חיפוש וכו') — ברירת מחדל: מספר הטלפון */
  children?: React.ReactNode;
  size?: 'small' | 'medium';
  onClick?: (e: React.MouseEvent) => void;
}

const PhoneLinkChip: React.FC<PhoneLinkChipProps> = ({
  phone,
  variant,
  suffix,
  children,
  size = 'small',
  onClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [copied, setCopied] = useState(false);

  const phoneDigits = String(phone || '').replace(/\D/g, '');
  const callable = phoneDigits.length >= 9;
  const isStore = variant === 'store';
  const useTelLink = callable && isMobile;

  const height = size === 'small' ? 28 : 32;
  const fontSize = size === 'small' ? '0.72rem' : '0.8rem';

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick?.(e);
      if (!callable) return;
      if (isMobile) return;
      e.preventDefault();
      const ok = await copyToClipboard(phoneDigits);
      if (ok) {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      }
    },
    [callable, isMobile, onClick, phoneDigits],
  );

  const chip = (
    <Chip
      component={useTelLink ? 'a' : 'span'}
      href={useTelLink ? `tel:${phoneDigits}` : undefined}
      clickable={callable}
      size={size}
      label={
        <Box
          component="span"
          dir="ltr"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
        >
          {children ?? phone}
          {suffix}
        </Box>
      }
      onClick={handleClick}
      sx={{
        height,
        maxWidth: '100%',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: callable
          ? isStore
            ? 'primary.main'
            : 'success.main'
          : 'divider',
        bgcolor: callable
          ? isStore
            ? 'primary.main'
            : 'success.main'
          : 'action.hover',
        color: callable ? '#fff' : 'text.secondary',
        fontWeight: 700,
        fontSize,
        '& .MuiChip-label': {
          px: size === 'small' ? 0.85 : 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        ...(callable && {
          cursor: 'pointer',
          textDecoration: 'none',
          '&:hover': {
            bgcolor: isStore ? 'primary.dark' : 'success.dark',
            borderColor: isStore ? 'primary.dark' : 'success.dark',
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
        }),
      }}
    />
  );

  if (!callable || isMobile) {
    return chip;
  }

  return (
    <Tooltip title={copied ? 'הועתק!' : 'לחץ להעתקה'} arrow placement="top">
      {chip}
    </Tooltip>
  );
};

export default PhoneLinkChip;
